<template>
  <TemplateLidBase>
    <div class="hidden"><!-- This health check is used by Exonet.  Do NOT remove it from the homepage! -->app-health-check:landelijkeInspectiedienst</div>
    <main class="relative z-0 col-span-full row-start-2">
      <MoleculeLidHeaderOverview
        class="mb-12 md:mb-16 xl:mb-20 2xl:mb-32"
        :dark-title="true"
        :position-text-left="true"
        :pre-title="page.preTitle"
        :title="page.title"
        :introduction="page.introduction"
        :image="page.image"
        :class="{ 'mt-16 lg:mt-20': !page.image }"
      >
        <template #headerButtons>
          <div v-if="page.references.length" class="flex-wrap mt-4 md:mt-6 sm:flex lg:mt-8 xl:mt-10">
            <AtomButton
              v-for="(reference, index) in page.references"
              :key="index"
              button-type="lid-tertiary"
              :url="reference.url"
              class="header-button"
              :class="{ 'sm:ml-4 md:ml-6 lg:ml-8 xl:ml-10': index > 0, 'mb-3': page.references.length > 1 && index !== page.references.length - 1 }"
            >
              {{ t_('highlight-button-text') }}
            </AtomButton>
          </div>
        </template>
      </MoleculeLidHeaderOverview>

      <section v-if="page.referenceBlock" class="relative z-10 mb-12 md:mb-16 xl:mb-20 2xl:mb-32 mx-4 lg:mx-6 xl:mx-12 2xl:mx-56">
        <MoleculeLidHighlightBanner :image="page.referenceBlock.image">
          <h2 class="font-black text-xl sm:text-2xl lg:text-3xl xl:text-5xl 2xl:text-6xl leading-none">
            {{ page.referenceBlock.label }}
          </h2>
          <div v-if="page.referenceBlock.introduction" class="prose prose--app prose--app--lid" v-html="page.referenceBlock.introduction" />
          <AtomButton v-if="page.referenceBlock.buttonUrl" :url="page.referenceBlock.buttonUrl" button-type="lid-read-more" class="pr-2">
            {{ page.referenceBlock.buttonLabel }}
            <AtomIconArrowRight class="transform transition ease-in-out duration-200 ml-6 w-4 xl:ml-8 xl:w-5 group-hover:translate-x-2" />
          </AtomButton>
        </MoleculeLidHighlightBanner>
      </section>

      <section v-if="page.articles.length" class="relative z-10 mb-12 md:mb-16 xl:mb-20 2xl:mb-32 mx-4 lg:mx-6 xl:mx-12">
        <div class="sm:justify-between sm:items-center mb-5 md:mb-6 lg:mb-8 xl:mb-12 2xl:mb-16 flex flex-col sm:flex-row gap-4">
          <h2 class="font-black text-xl sm:text-2xl lg:text-3xl xl:text-5xl 2xl:text-6xl leading-none">{{ page.articleTitle || t_('all-articles-title') }}</h2>
          <AtomButton :url="articleOverviewUrl" button-type="lid-primary" class="hidden sm:flex header-button md:ml-auto">
            {{ t_('all-articles-button') }}
          </AtomButton>
        </div>
        <MoleculeCarouselLidCarousel :slides="page.articles.slice(0, 3)" type="slider">
          <template #slide="{ slide }">
            <OrganismLoaderFromUri v-slot="article" :site="slide.site" :uri="slide.uri">
              <MoleculeCarouselLidCarouselSlideStory :date="article.date" :url="article.url" :title="article.title" :image="article.image" />
            </OrganismLoaderFromUri>
          </template>
        </MoleculeCarouselLidCarousel>
        <AtomButton :url="articleOverviewUrl" button-type="lid-primary" class="sm:hidden mt-6 header-button md:ml-auto">
          {{ t_('all-articles-button') }}
        </AtomButton>
      </section>
    </main>
  </TemplateLidBase>
</template>

<script setup lang="ts">
import { useGlobalStore } from '~/store/global'
import { setupTranslationPrefixer } from '~/composables/util'

interface Page {
  preTitle: string
  title: string
  introduction: string
  image: string
  references: Array<{ url: string }>
  referenceBlock?: {
    image: string
    label: string
    introduction?: string
    buttonUrl?: string
    buttonLabel?: string
  }
  articles: Array<{ site: string; uri: string }>
  articleTitle?: string
}

interface Props {
  page?: Page
}

withDefaults(defineProps<Props>(), {
  page: () => ({
    preTitle: '',
    title: '',
    introduction: '',
    image: '',
    references: [],
    articles: [],
  }),
})

const globalStore = useGlobalStore()
const articleOverviewUrl = globalStore.articleOverview?.url

const { t_ } = setupTranslationPrefixer('lid-landing-detail')
</script>

<style lang="css" scoped>
.header-button .button {
  @screen 2xl {
    min-height: 84px;
  }
}
</style>
